import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Globe, Settings2, CheckCircle2, Timer, TriangleAlert } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, LoadingState } from '@/components/common';
import { Card, CardContent, CardHeader, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { permitsApi, type CountryRegime, type LeadTimeReport } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { cn } from '@/lib/utils';

export default function PermitCountries() {
  const [countries, setCountries] = useState<CountryRegime[]>([]);
  const [report, setReport] = useState<LeadTimeReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CountryRegime | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    alertLeadDays: '30',
    cumulativeAdmissionCap: '',
    approvalBodies: '',
    requireSecurityRecovery: true,
    acceptDischargeCertificate: false,
    acceptProofOfExit: true,
  });

  const load = async () => {
    try {
      setLoading(true);
      const [list, measured] = await Promise.all([
        permitsApi.getCountries(),
        // Measured approval time belongs next to the lead time it should
        // inform, not on a report nobody opens while setting one.
        permitsApi.getLeadTimeReport().catch(() => null),
      ]);
      setCountries(list);
      setReport(measured);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openEditor = (country: CountryRegime) => {
    setEditing(country);
    setForm({
      alertLeadDays: String(country.config?.alertLeadDays ?? 30),
      cumulativeAdmissionCap: country.config?.cumulativeAdmissionCap?.toString() ?? '',
      approvalBodies: (country.config?.approvalBodies ?? country.authorityChain).join(', '),
      requireSecurityRecovery: country.config?.requireSecurityRecovery ?? true,
      acceptProofOfExit:
        country.config?.acceptedDischargeEvidence?.includes('PROOF_OF_EXIT') ?? true,
      acceptDischargeCertificate:
        country.config?.acceptedDischargeEvidence?.includes('CUSTOMS_DISCHARGE_CERTIFICATE') ??
        false,
    });
  };

  const save = async () => {
    if (!editing) return;
    try {
      setSaving(true);
      await permitsApi.saveCountryConfig({
        countryRegimeId: editing.id,
        alertLeadDays: Number(form.alertLeadDays),
        // Blank means no cap, which is different from a cap of zero.
        cumulativeAdmissionCap: form.cumulativeAdmissionCap
          ? Number(form.cumulativeAdmissionCap)
          : null,
        approvalBodies: form.approvalBodies
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        requireSecurityRecovery: form.requireSecurityRecovery,
        acceptedDischargeEvidence: [
          ...(form.acceptProofOfExit ? (['PROOF_OF_EXIT'] as const) : []),
          ...(form.acceptDischargeCertificate ? (['CUSTOMS_DISCHARGE_CERTIFICATE'] as const) : []),
        ],
      });
      toast.success(`${editing.countryName} settings saved`);
      setEditing(null);
      await load();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingState />;

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Country Configuration"
          description="Temporary-admission rules by country, with the configured limits applied to each."
        />
      </motion.div>

      <motion.div variants={fadeUp} className="mt-4">
        <div className="rounded-lg border border-border bg-muted p-4">
          <p className="text-sm text-muted-foreground">
            Customs rules such as régimes, authority chains and currency are the same for every operator in
            a country, so they are held once for the whole platform and maintained centrally. What
            you set here is your own: how far ahead to warn, who approves, and any cap agreed for
            your contracts.
          </p>
        </div>
      </motion.div>

      {countries.length === 0 && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card>
            <CardContent className="py-12 text-center">
              <Globe className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <p className="font-medium text-foreground">No countries configured</p>
              <p className="text-sm text-muted-foreground">
                A platform administrator adds a country's customs rules before it can be used.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {countries.map((c) => (
          <motion.div key={c.id} variants={fadeUp}>
            <Card className={cn(!c.isConfigured && 'border-dashed')}>
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold text-foreground">{c.countryName}</h3>
                      <Badge variant="outline" className="font-mono text-xs">
                        {c.countryCode}
                      </Badge>
                      {c.isConfigured ? (
                        <Badge className="bg-status-success-subtle text-status-success-foreground">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          in use
                        </Badge>
                      ) : (
                        <Badge variant="outline">not set up</Badge>
                      )}
                    </div>
                    <CardDescription className="mt-1">
                      {c.renewalModel === 'PROROGATION_STACK'
                        ? 'Renewals stack against one case, each with its own duty'
                        : 'Each renewal is a new linked case'}
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => openEditor(c)}>
                    <Settings2 className="mr-2 h-4 w-4" />
                    {c.isConfigured ? 'Settings' : 'Set up'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
                  <div>
                    <dt className="text-xs text-muted-foreground">Currencies</dt>
                    <dd className="mt-0.5 text-sm font-medium">{c.currencies.join(', ') || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Authority chain</dt>
                    <dd className="mt-0.5 text-sm font-medium">
                      {c.authorityChain.join(' → ') || '—'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Alert lead time</dt>
                    <dd
                      className={cn(
                        'mt-0.5 text-sm font-medium',
                        !c.config && 'text-muted-foreground'
                      )}
                    >
                      {c.config ? `${c.config.alertLeadDays} days` : 'not set'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-muted-foreground">Admission cap</dt>
                    <dd
                      className={cn(
                        'mt-0.5 text-sm font-medium',
                        !c.config?.cumulativeAdmissionCap && 'text-muted-foreground'
                      )}
                    >
                      {c.config?.cumulativeAdmissionCap
                        ? `${c.config.cumulativeAdmissionCap} days`
                        : 'none'}
                    </dd>
                  </div>
                </dl>
                {(() => {
                  const measured = report?.countries.find((r) => r.countryCode === c.countryCode);
                  if (!measured) return null;
                  return (
                    <div
                      className={cn(
                        'mt-3 rounded-lg border p-3',
                        measured.leadTimeTooShort
                          ? 'border-status-warning-border bg-status-warning-subtle'
                          : 'border-border bg-muted'
                      )}
                    >
                      <div className="flex items-start gap-2">
                        {measured.leadTimeTooShort ? (
                          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-status-warning-foreground" />
                        ) : (
                          <Timer className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground">
                            Approvals here take {measured.chainMedianDays} days
                            {measured.slowestStage && `, most of it at ${measured.slowestStage}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {measured.leadTimeTooShort
                              ? `A ${measured.configuredLeadDays}-day warning arrives after a renewal would have had to start. Raise the lead time above ${measured.chainMedianDays} days.`
                              : `Median across ${measured.authorities.reduce((n, a) => n + a.samples, 0)} recorded stages.`}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
                {(c.showCustomsDeclaration || c.showExemptionFlag) && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {c.showCustomsDeclaration && (
                      <Badge variant="outline" className="text-xs">
                        customs declaration
                      </Badge>
                    )}
                    {c.showExemptionFlag && (
                      <Badge variant="outline" className="text-xs">
                        exemption flag
                      </Badge>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.countryName} settings</DialogTitle>
            <DialogDescription>
              Your company's settings for this country. The customs rules themselves are shared
              across the platform and are not editable here.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="lead">Alert lead time (days)</Label>
              <Input
                id="lead"
                type="number"
                value={form.alertLeadDays}
                onChange={(e) => setForm({ ...form, alertLeadDays: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Set this above the time an approval actually takes here, so a warning still leaves
                room to complete a renewal.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cap">Cumulative admission cap (days)</Label>
              <Input
                id="cap"
                type="number"
                value={form.cumulativeAdmissionCap}
                onChange={(e) => setForm({ ...form, cumulativeAdmissionCap: e.target.value })}
                placeholder="Leave blank for no cap"
              />
              <p className="text-xs text-muted-foreground">
                Total time an asset may stay, however many renewals are granted. A renewal past this
                is refused.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bodies">Approval bodies</Label>
              <Input
                id="bodies"
                value={form.approvalBodies}
                onChange={(e) => setForm({ ...form, approvalBodies: e.target.value })}
                placeholder="Comma separated"
              />
            </div>
            <div className="space-y-2 rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Accepted discharge evidence</p>
                <p className="text-xs text-muted-foreground">
                  What this authority will accept as proof the asset left. An obligation cannot be
                  discharged with anything else.
                </p>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Proof of exit</span>
                <Switch
                  checked={form.acceptProofOfExit}
                  onCheckedChange={(v) => setForm({ ...form, acceptProofOfExit: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm">Customs discharge certificate</span>
                <Switch
                  checked={form.acceptDischargeCertificate}
                  onCheckedChange={(v) => setForm({ ...form, acceptDischargeCertificate: v })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Guarantee recovery required to close</p>
                <p className="text-xs text-muted-foreground">
                  A case stays open until the security is back from customs.
                </p>
              </div>
              <Switch
                checked={form.requireSecurityRecovery}
                onCheckedChange={(v) => setForm({ ...form, requireSecurityRecovery: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
